import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle2, XCircle, Loader2, ExternalLink, Copy, Linkedin, Twitter, ArrowLeft, Anchor } from 'lucide-react';
import { Navbar, Footer } from '@/components/Layout';
import { DefenseKitButton } from '@/components/binding/DefenseKitButton';
import { RevokeButton } from '@/components/binding/RevokeButton';
import { GateProposedNotice } from '@/components/binding/GateProposedNotice';
import { fetchReceipt, verifyMerkleProof, shareUrl, fmtDay } from '@/lib/bindingConsent';

const STATUS = {
  consumed: { label: 'Executed through the gate', cls: 'text-emerald-300 border-emerald-500/40 bg-emerald-500/10' },
  unused: { label: 'Signed, unused. Gate paused.', cls: 'text-cyan-300 border-cyan-500/40 bg-cyan-500/10' },
  expired: { label: 'Expired unused', cls: 'text-gray-400 border-white/15 bg-white/5' },
  revoked: { label: 'Revoked by the signer', cls: 'text-red-300 border-red-500/40 bg-red-500/10' },
};

const ANCHOR_TEXT = {
  anchored: 'Anchored on Base. The Merkle root for this day is in the transaction calldata.',
  submitted: 'Anchor transaction submitted. Waiting for confirmation.',
  sending: 'Anchor transaction being sent.',
  unfunded: 'Merkle root computed. Not yet on Base: the anchor wallet is awaiting funding.',
  unconfigured: 'Merkle root computed. Anchor wallet not configured on this environment.',
  failed: 'Anchor attempt failed. It retries on the next daily run.',
  pending_day: 'This UTC day is still open. Its receipts are anchored after midnight UTC.',
  pending: 'Waiting for the next daily anchor run.',
};

function Row({ label, value, testid, mono = true }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-start gap-1 sm:gap-4 py-2 border-b border-white/5 last:border-0">
      <span className="text-[11px] uppercase tracking-widest text-gray-500 sm:w-44 shrink-0 pt-0.5">{label}</span>
      <span className={`text-sm text-gray-200 break-all ${mono ? 'font-mono' : ''}`} data-testid={testid}>{value}</span>
    </div>
  );
}

function ShareBar({ receiptId }) {
  const [copied, setCopied] = useState(false);
  const url = shareUrl(receiptId);
  const text = 'I signed a consent receipt and watched the Binding Action Gate check it. Agents should not sign for you by default.';
  const copy = async () => { await navigator.clipboard.writeText(url); setCopied(true); setTimeout(() => setCopied(false), 1500); };
  const btn = 'inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-sm text-gray-200 hover:border-white/40 transition-colors';
  return (
    <div className="flex flex-wrap gap-2" data-testid="receipt-share-bar">
      <a className={btn} href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`} target="_blank" rel="noopener noreferrer" data-testid="receipt-share-linkedin"><Linkedin className="w-4 h-4" /> Share on LinkedIn</a>
      <a className={btn} href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`} target="_blank" rel="noopener noreferrer" data-testid="receipt-share-x"><Twitter className="w-4 h-4" /> Share on X</a>
      <button type="button" className={btn} onClick={copy} data-testid="receipt-share-copy"><Copy className="w-4 h-4" /> {copied ? 'Link copied' : 'Copy share link'}</button>
    </div>
  );
}

function FeeRow({ fee }) {
  if (!fee) return null;
  if (fee.status === 'paid') {
    return (
      <Row label="Execution fee" testid="receipt-fee" mono={false} value={
        <span className="inline-flex flex-wrap items-center gap-2">
          <span>${fee.amount_usdc.toFixed(2)} USDC, paid</span>
          <a href={fee.explorer_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-red-300 hover:text-red-200" data-testid="receipt-fee-tx-link">View payment on BaseScan <ExternalLink className="w-3.5 h-3.5" /></a>
        </span>
      } />
    );
  }
  const text = { free_demo: 'Free (demo receipt)', paid_unlogged: '$0.10 USDC, paid (before payment links were recorded)', not_charged: 'Not charged (not executed)' }[fee.status];
  return <Row label="Execution fee" value={text} testid="receipt-fee" mono={false} />;
}

function AnchorCard({ r }) {
  const a = r.anchor || {};
  const localOk = r.proof && a.root ? verifyMerkleProof(r.receipt_id, r.proof, a.root) : null;
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-6" data-testid="receipt-anchor-card">
      <div className="flex items-center gap-2 mb-3"><Anchor className="w-4 h-4 text-red-300" /><h2 className="text-sm uppercase tracking-widest text-gray-400 font-semibold">Daily Base anchor</h2></div>
      <p className="text-sm text-gray-300 mb-3" data-testid="receipt-anchor-status">{ANCHOR_TEXT[a.status] || a.status}</p>
      <Row label="Anchor day (UTC)" value={fmtDay(r.day)} testid="receipt-anchor-day" mono={false} />
      {a.root && <Row label="Merkle root" value={a.root} testid="receipt-anchor-root" />}
      {a.leaf_count != null && <Row label="Receipts that day" value={String(a.leaf_count)} testid="receipt-anchor-count" />}
      {a.anchor_address && <Row label="Anchor wallet" value={a.anchor_address} testid="receipt-anchor-address" />}
      {a.explorer_url && (
        <a href={a.explorer_url} target="_blank" rel="noopener noreferrer" className="mt-3 inline-flex items-center gap-1.5 text-sm text-red-300 hover:text-red-200" data-testid="receipt-anchor-tx-link">
          View anchor transaction on BaseScan <ExternalLink className="w-3.5 h-3.5" />
        </a>
      )}
      {r.proof && (
        <div className="mt-4" data-testid="receipt-proof">
          <div className="text-[11px] uppercase tracking-widest text-gray-500 mb-2">Merkle proof ({r.proof.length} steps)</div>
          <ul className="space-y-1 text-[11px] font-mono text-gray-400 break-all">{r.proof.map((p) => <li key={p}>{p}</li>)}</ul>
          <div className={`mt-3 inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-bold uppercase tracking-widest ${localOk ? 'text-emerald-300 border-emerald-500/40' : 'text-red-300 border-red-500/40'}`} data-testid="receipt-proof-result">
            {localOk ? <CheckCircle2 className="w-3.5 h-3.5" /> : <XCircle className="w-3.5 h-3.5" />}
            {localOk ? 'Proof verified in your browser' : 'Proof does not verify'}
          </div>
          <p className="text-[11px] text-gray-500 mt-2">{a.scheme}</p>
        </div>
      )}
    </div>
  );
}

export default function BindingReceipt() {
  const { receiptId } = useParams();
  const [r, setR] = useState(null);
  const [err, setErr] = useState(null);
  const load = () => fetchReceipt(receiptId).then(setR).catch(() => setErr('Receipt not found.'));
  useEffect(() => { load(); }, [receiptId]); // eslint-disable-line react-hooks/exhaustive-deps
  const st = r ? STATUS[r.status] || STATUS.unused : null;
  return (
    <div className="min-h-screen bg-[#050505] text-white" data-testid="binding-receipt-page">
      <Helmet><title>Consent receipt | Binding Action Gate | Kojiru</title></Helmet>
      <Navbar />
      <section className="pt-28 pb-16 px-6">
        <div className="container mx-auto max-w-4xl">
          <Link to="/binding-gate" className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest text-gray-400 hover:text-white mb-6" data-testid="receipt-back-link"><ArrowLeft className="w-3.5 h-3.5" /> Binding Action Gate (proposed)</Link>
          <div className="mb-8"><GateProposedNotice testid="receipt-proposed-notice" compact /></div>
          {!r && !err && <div className="py-20 flex justify-center"><Loader2 className="w-7 h-7 text-red-400 animate-spin" /></div>}
          {err && <p className="text-red-300" data-testid="receipt-error">{err}</p>}
          {r && (
            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
              <div>
                <div className="text-xs uppercase tracking-widest text-red-300 mb-3">Consent receipt</div>
                <h1 className="text-4xl sm:text-5xl font-bold leading-tight mb-4">A human said yes to this exact document.</h1>
                <span className={`inline-flex rounded-full border px-3 py-1 text-xs font-bold uppercase tracking-widest ${st.cls}`} data-testid="receipt-status">{st.label}</span>
              </div>
              <ShareBar receiptId={r.receipt_id} />
              <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-6" data-testid="receipt-details">
                <Row label="Fingerprint" value={r.receipt_id} testid="receipt-fingerprint" />
                <Row label="Signer" value={r.principal} testid="receipt-signer" />
                <Row label="Document hash" value={r.document_hash} testid="receipt-document-hash" />
                <Row label="Agent" value={r.kypa_id} testid="receipt-agent" />
                <Row label="Signed" value={new Date(r.created_at).toLocaleString('en-US', { month: 'long', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', timeZone: 'UTC' }) + ' UTC'} testid="receipt-signed-at" mono={false} />
                {r.consumed_at && <Row label="Executed" value={new Date(r.consumed_at).toLocaleString('en-US', { month: 'long', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', timeZone: 'UTC' }) + ' UTC'} testid="receipt-executed-at" mono={false} />}
                {r.revoked_at && <Row label="Revoked" value={new Date(r.revoked_at).toLocaleString('en-US', { month: 'long', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', timeZone: 'UTC' }) + ' UTC'} testid="receipt-revoked-at" mono={false} />}
                <FeeRow fee={r.fee} />
                <Row label="Signed notice" value={r.notice ?? 'Private (real receipt)'} testid="receipt-notice" mono={false} />
              </div>
              <RevokeButton receipt={r} onRevoked={load} />
              <AnchorCard r={r} />
              {r.kypa_id !== 'KYPA-DEMO-SANDBOX' && <DefenseKitButton receipt={r} />}
            </motion.div>
          )}
        </div>
      </section>
      <Footer />
    </div>
  );
}
